import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Activity, ChevronDown, ArrowLeft } from 'lucide-react';
import { api, getMunicipioSlug } from '../services/api';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
import { useLocation as useTenantLocation } from '../context/LocationContext';
import { FEED_CHIPS } from '../utils/feedHelpers';
import FeedCard from '../components/FeedCard';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Feed Público da Cidade — /feed e /:municipioSlug/feed.
 *
 * - Chips de filtro horizontais (negocio_categoria ou categoria_post).
 * - Cursor-based infinite load via "Carregar mais" + auto-scroll observer.
 * - Filtra no cliente posts expirados (defesa em camadas além do backend).
 */
export default function FeedPublico() {
  const { municipioSlug } = useParams();
  const navigate = useNavigate();
  useTenantGeoRedirect({ routeName: 'feed', currentSlug: municipioSlug });
  const { currentLocation } = useTenantLocation() || {};

  const slug = municipioSlug || getMunicipioSlug();
  const nomeMunicipioRaw = currentLocation?.nome || currentLocation?.name || (slug ? slug.replace(/-/g, ' ') : 'sua cidade');
  const nomeMunicipio = nomeMunicipioRaw
    .split(' ')
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chipKey, setChipKey] = useState('todos');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const sentinelRef = useRef(null);

  const filtroAtivo = FEED_CHIPS.find((c) => c.key === chipKey) || FEED_CHIPS[0];

  // labels: 1x
  useEffect(() => {
    api.get('/v2/feed/categorias')
      .then((r) => {
        const m = {};
        (r.data?.categorias || []).forEach((c) => { m[c.key] = c.label; });
        setLabels(m);
      })
      .catch(() => setLabels({}));
  }, []);

  const fetchFeed = useCallback(async ({ reset = false } = {}) => {
    if (reset) {
      setLoading(true);
      setCursor(null);
    } else {
      setLoadingMore(true);
    }
    try {
      const params = { municipio_slug: slug || '', limit: 20 };
      if (filtroAtivo.negocio_categoria) params.negocio_categoria = filtroAtivo.negocio_categoria;
      if (filtroAtivo.categoria_post) params.categoria_post = filtroAtivo.categoria_post;
      if (!reset && cursor) params.cursor = cursor;
      const r = await api.get('/v2/feed', { params });
      const novos = (r.data?.items || []).filter((it) => it.status === 'ativo' && new Date(it.expira_em).getTime() > Date.now());
      setItems((prev) => (reset ? novos : [...prev, ...novos]));
      setCursor(r.data?.next_cursor || null);
      setHasMore(!!r.data?.next_cursor);
    } catch {
      if (reset) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, chipKey, cursor]);

  // recarga sempre que mudar chip ou slug
  useEffect(() => {
    fetchFeed({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, chipKey]);

  // IntersectionObserver para auto-load
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loadingMore && !loading) {
        fetchFeed({ reset: false });
      }
    }, { rootMargin: '120px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadingMore, fetchFeed]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="feed-publico-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Breadcrumbs items={[{ label: 'Feed' }]} className="mb-3" />

        <header className="mb-5">
          {/* Botão Voltar visível ao lado do título */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(slug ? `/${slug}` : '/'))}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 hover:text-purple-900 hover:bg-purple-50 -ml-2 px-2 py-1 rounded-full transition mb-3"
            data-testid="feed-back-button"
            aria-label="Voltar para a página anterior"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-6 h-6 text-purple-600" />
            <h1 className="text-2xl md:text-3xl font-bold">Acontecendo agora em {nomeMunicipio}</h1>
          </div>
          <p className="text-sm text-gray-600">
            {loading ? 'Carregando…' : `${items.length}${hasMore ? '+' : ''} ${items.length === 1 ? 'post ativo' : 'posts ativos'} agora`}
          </p>
        </header>

        {/* chips */}
        <div className="overflow-x-auto -mx-4 px-4 mb-5" data-testid="feed-chips">
          <div className="inline-flex gap-2 pb-1">
            {FEED_CHIPS.map((c) => (
              <button
                key={c.key}
                onClick={() => setChipKey(c.key)}
                data-testid={`feed-chip-${c.key}`}
                className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium transition border ${
                  chipKey === c.key
                    ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                }`}
              >
                <span className="mr-1">{c.emoji}</span>{c.label}
              </button>
            ))}
          </div>
        </div>

        {/* feed */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Carregando o feed…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <p className="text-gray-700 font-medium">Ainda não há novidades nesse filtro.</p>
            <p className="text-sm text-gray-500 mt-1">Volte daqui a pouco — o feed é em tempo real.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((p, idx) => (
              <FeedCard key={`${p.id || p._id || 'post'}-${idx}`} post={p} variant="full" categoriasMap={labels} />
            ))}
          </div>
        )}

        {/* sentinela + carregar mais */}
        <div ref={sentinelRef} />
        {hasMore && !loading && (
          <div className="text-center mt-6">
            <button
              onClick={() => fetchFeed({ reset: false })}
              disabled={loadingMore}
              data-testid="feed-load-more"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-white border border-gray-200 hover:border-purple-300 text-sm font-medium transition disabled:opacity-50"
            >
              {loadingMore ? 'Carregando…' : (<>Carregar mais <ChevronDown className="w-4 h-4" /></>)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
